/**
 * Utility functions for Archie Widgets
 */

/**
 * Try to parse data as JSON
 * @param {any} data - Data to parse
 * @returns {Object|null} Parsed JSON object or null
 */
export const tryParseJson = (data) => {
    if (typeof data === 'object') {
        return data;
    }
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch (e) {
            return null;
        }
    }
    return null;
};

/**
 * Format a metric key to a human-readable label
 * @param {string} key - The metric key (e.g., 'embodied_carbon_kgCO2e')
 * @returns {string} Formatted label (e.g., 'Embodied Carbon KgCO2e')
 */
export const formatMetricLabel = (key) => {
    return key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Check if JSON data is a valid hierarchical KPI structure
 * @param {Object} jsonData - Parsed JSON data
 * @returns {boolean} True if it's a valid KPI structure
 */
export const isValidKpiJsonStructure = (jsonData) => {
    if (!jsonData || typeof jsonData !== 'object' || Array.isArray(jsonData)) {
        return false;
    }
    
    const keys = Object.keys(jsonData);
    if (keys.length === 0) {
        return false;
    }
    
    const firstKey = keys[0];
    const firstValue = jsonData[firstKey];
    
    // Check if the first level is an object with nested objects (system -> subsystems)
    if (typeof firstValue === 'object' && !Array.isArray(firstValue)) {
        const subsystemKeys = Object.keys(firstValue);
        if (subsystemKeys.length > 0) {
            const firstSubsystem = firstValue[subsystemKeys[0]];
            
            // Check if subsystem contains metrics
            if (typeof firstSubsystem === 'object' && !Array.isArray(firstSubsystem)) {
                return true;
            }
        }
    }
    
    return false;
};

/**
 * Detect metric type based on key name patterns
 * @param {string} metricKey - The metric key name
 * @returns {string} Metric type: 'carbon', 'energy', 'water', 'shadow', 'annotation', 'default'
 */
export const detectMetricType = (metricKey) => {
    const lowerKey = metricKey.toLowerCase();
    
    // Check for annotation/description fields
    if (lowerKey.includes('annotation') || lowerKey.includes('description') || lowerKey.includes('note')) {
        return 'annotation';
    }
    
    // Check for carbon/environmental metrics
    if (lowerKey.includes('carbon') || lowerKey.includes('co2') || lowerKey.includes('ghg') || lowerKey.includes('emission')) {
        return 'carbon';
    }
    
    // Check for energy metrics
    if (lowerKey.includes('energy') || lowerKey.includes('eui') || lowerKey.includes('kwh') || lowerKey.includes('power')) {
        return 'energy';
    }
    
    // Check for shadow metrics
    if (lowerKey.includes('shadow') || lowerKey.includes('shade') || lowerKey.includes('sunlight')) {
        return 'shadow';
    }
    
    // Check for water metrics
    if (lowerKey.includes('water') || lowerKey.includes('h2o')) {
        return 'water';
    }
    
    return 'default';
};

/**
 * Get icon and styling for a metric type
 * @param {string} metricType - Type returned by detectMetricType
 * @returns {Object} Object with icon and className
 */
export const getMetricStyle = (metricType) => {
    const styles = {
        carbon: { icon: '🌱', className: 'carbon', theme: 'environmental' },
        energy: { icon: '⚡', className: 'energy', theme: 'energy' },
        water: { icon: '💧', className: 'water', theme: 'water' },
        shadow: { icon: '🌓', className: 'shadow', theme: 'shadow' },
        annotation: { icon: '📝', className: 'annotation', theme: 'info' },
        default: { icon: '📊', className: 'default', theme: 'default' }
    };
    
    return styles[metricType] || styles.default;
};

/**
 * Format numeric value with appropriate decimal places
 * @param {string|number} value - The value to format
 * @returns {string} Formatted value
 */
export const formatMetricValue = (value) => {
    // Check if value is a number or numeric string
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
        // Format with appropriate decimal places
        if (numValue >= 1000) {
            return numValue.toLocaleString(undefined, { maximumFractionDigits: 1 });
        } else if (numValue >= 10) {
            return numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
        } else {
            return numValue.toLocaleString(undefined, { maximumFractionDigits: 3 });
        }
    }
    return value;
};
